import React, {useEffect, useMemo, useRef, useState} from "react";
import {fetchBellMusicForDropDown} from "../../common-services";
import {T} from "./i18n";
import {CSS} from "./styles";
import {BellMark, PlayIcon} from "./icons";
import {blankRow, getMusicId, num, pretty, toHM, toMin} from "./helpers";
import {useBellPreview} from "./useBellPreview";
import DayRibbon from "./DayRibbon";
import MusicSelect from "./MusicSelect";

const Field = ({label, children}) => (
    <label className="dg-field">
        <span>{label}</span>
        {children}
    </label>
);

// How long each bell rings while auditioning the whole day, so a full schedule
// previews in a reasonable time regardless of the saved play time.
const DAY_STEP_MS = 3500;

/**
 * Add Bell schedule modal.
 *
 * Music options are loaded from the API (/bell/music/active/min) and every
 * preview goes through the shared remote-first + /public/bells fallback player.
 * `onSave` receives a list ready for the bell-save DTO:
 *   { name, time, playTime, type, music }  (music = the chosen option's URL)
 */
export function BellScheduleModal({
                                      open = true,
                                      onClose = () => {
                                      },
                                      onSave = (payload) => console.log("SAVE →", payload),
                                      lang = "ne",
                                  }) {
    const [L, setL] = useState(lang);
    const t = T[L];
    const preview = useBellPreview();

    const [musicList, setMusicList] = useState([]);
    const [rows, setRows] = useState(() => [blankRow()]);
    const [activeId, setActiveId] = useState(null);
    const [toast, setToast] = useState("");
    const [quickOpen, setQuickOpen] = useState(false);
    const [dayPlaying, setDayPlaying] = useState(false);
    const [qf, setQf] = useState({start: "10:00", len: 45, count: 7, after: 4, brk: 30});

    const toastTimer = useRef(null);
    const focusNext = useRef(null);
    const dayTimers = useRef([]);

    const say = (m) => {
        setToast(m);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(""), 2200);
    };

    // Load the bell-music dropdown once, when the modal opens.
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetchBellMusicForDropDown();
                if (!cancelled && response?.status === 200) {
                    setMusicList(response.data || []);
                }
            } catch (e) {
                console.error("Error while fetching bell music dropdown:", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    const defaultMusic = musicList[0] || null;

    const clearDayTimers = () => {
        dayTimers.current.forEach(clearTimeout);
        dayTimers.current = [];
    };

    const stopEverything = () => {
        clearDayTimers();
        preview.stop();
        setDayPlaying(false);
    };

    const close = () => {
        stopEverything();
        setActiveId(null);
        onClose();
    };

    // Esc to close + lock background scroll while open.
    useEffect(() => {
        if (!open) return undefined;
        const esc = (e) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", esc);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", esc);
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => () => clearDayTimers(), []);

    // Bells ringing within a minute of each other are almost always a mistake.
    const clashSet = useMemo(() => {
        const s = rows.filter((r) => r.time).sort((a, b) => toMin(a.time) - toMin(b.time));
        const bad = new Set();
        for (let i = 1; i < s.length; i++) {
            if (toMin(s[i].time) - toMin(s[i - 1].time) < 1) {
                bad.add(s[i].id);
                bad.add(s[i - 1].id);
            }
        }
        return bad;
    }, [rows]);

    const patch = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? {...r, [k]: v} : r)));

    const ringOne = (r) => {
        stopEverything();
        setActiveId(r.id);
        preview.play(`row:${r.id}`, r.music?.url);
    };

    const addRow = (afterId = null) => {
        setRows((rs) => {
            const i = afterId == null ? rs.length - 1 : rs.findIndex((r) => r.id === afterId);
            const src = rs[i];
            const next = blankRow(
                src
                    ? {
                        time: src.time ? toHM(Math.min(1439, toMin(src.time) + 45)) : "",
                        music: src.music,
                        dur: src.dur,
                    }
                    : {music: defaultMusic}
            );
            focusNext.current = next.id;
            const out = [...rs];
            out.splice(i + 1, 0, next);
            return out;
        });
    };

    // Move focus to the freshly-added row's name field.
    useEffect(() => {
        if (!focusNext.current) return;
        const el = document.querySelector(`[data-focus="${focusNext.current}"]`);
        if (el) el.focus();
        focusNext.current = null;
    }, [rows]);

    const runQuickFill = () => {
        const start = toMin(qf.start) ?? 600;
        const len = num(qf.len, 45);
        const cnt = num(qf.count, 7);
        const after = num(qf.after, 0);
        const brk = num(qf.brk, 0);

        const out = [
            blankRow({name: t.presets.assembly, time: toHM(Math.max(0, start - 15)), music: defaultMusic, dur: 20}),
        ];
        let cur = start;
        for (let p = 1; p <= cnt; p++) {
            out.push(blankRow({name: t.presets.p + " " + p, time: toHM(cur), music: defaultMusic, dur: 10}));
            cur += len;
            if (p === after && brk > 0) {
                out.push(blankRow({name: t.presets.lunch, time: toHM(cur), music: defaultMusic, dur: 15}));
                cur += brk;
            }
        }
        out.push(blankRow({name: t.presets.dismiss, time: toHM(cur), music: defaultMusic, dur: 20}));
        setRows(out);
        setQuickOpen(false);
        say("⚡ " + out.length + " " + t.countU);
    };

    // Audition the whole day: ring each placed bell in turn for a fixed window.
    const playDay = () => {
        if (dayPlaying) {
            stopEverything();
            setActiveId(null);
            return;
        }
        const placed = rows.filter((r) => r.time).sort((a, b) => toMin(a.time) - toMin(b.time));
        if (!placed.length) return;

        setDayPlaying(true);
        clearDayTimers();
        placed.forEach((r, i) => {
            dayTimers.current.push(
                setTimeout(() => {
                    setActiveId(r.id);
                    preview.play(`day:${r.id}`, r.music?.url);
                }, i * DAY_STEP_MS)
            );
        });
        dayTimers.current.push(
            setTimeout(() => {
                preview.stop();
                setDayPlaying(false);
                setActiveId(null);
            }, placed.length * DAY_STEP_MS)
        );
    };

    const save = () => {
        if (!rows.length) return;
        if (rows.some((r) => !r.time)) {
            say("⚠ " + t.noTime);
            return;
        }
        if (rows.some((r) => !r.music)) {
            say("⚠ " + t.noMusic);
            return;
        }
        onSave(
            rows.map((r) => ({
                name: r.name || null,
                time: r.time,
                playTime: num(r.dur, 10),
                type: r.music?.type ?? null,
                music: r.music?.url ?? null,
                musicId: getMusicId(r.music),
                musicLabel: r.music?.label ?? null,
            }))
        );
        say("✓ " + t.saved);
    };

    const placed = rows.filter((r) => r.time).sort((a, b) => toMin(a.time) - toMin(b.time));

    if (!open) return null;

    return (
        <div
            className="dg-backdrop"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) close();
            }}
        >
            <style>{CSS}</style>
            <div className="dg-modal" role="dialog" aria-modal="true" aria-label={t.title}>
                {/* header */}
                <header className="dg-head">
                    <BellMark/>
                    <div className="dg-head-txt">
                        <h2>{t.title}</h2>
                        <p>{t.sub}</p>
                    </div>
                    <div className="dg-head-act">
                        <div className="dg-lang" role="group">
                            <button type="button" aria-pressed={L === "ne"} onClick={() => setL("ne")}>
                                नेपाली
                            </button>
                            <button type="button" aria-pressed={L === "en"} onClick={() => setL("en")}>
                                EN
                            </button>
                        </div>
                        <button type="button" className="dg-icon" onClick={close} aria-label="Close">
                            ✕
                        </button>
                    </div>
                </header>

                <div className="dg-scroll">
                    {/* ribbon */}
                    <section className="dg-ribbon-wrap">
                        <div className="dg-ribbon-head">
                            <h3>{t.dayMap}</h3>
                            <span>{t.dayHint}</span>
                        </div>
                        <DayRibbon rows={rows} t={t} clashSet={clashSet} activeId={activeId} onPick={ringOne}/>
                        <div className="dg-legend">
                            <span><i style={{background: "#FFB305"}}/>{t.lgBell}</span>
                            <span><i style={{background: "#047E4F"}}/>{t.lgSoft}</span>
                            <span><i style={{background: "#0B66DB"}}/>{t.lgInfo}</span>
                            <span><i style={{background: "#FF6B4A"}}/>{t.lgAlert}</span>
                        </div>
                    </section>

                    {/* quick fill */}
                    <div className={"dg-quick" + (quickOpen ? " open" : "")}>
                        <button
                            type="button"
                            className="dg-quick-top"
                            onClick={() => setQuickOpen((v) => !v)}
                            aria-expanded={quickOpen}
                        >
                            <span>⚡</span>
                            <span>
                                <b>{t.qfTitle}</b>
                                <br/>
                                <small>{t.qfSub}</small>
                            </span>
                            <span className="dg-chev2">▾</span>
                        </button>
                        {quickOpen && (
                            <div className="dg-quick-body">
                                <Field label={t.qfStart}>
                                    <input type="time" value={qf.start}
                                           onChange={(e) => setQf({...qf, start: e.target.value})}/>
                                </Field>
                                <Field label={t.qfLen}>
                                    <input type="number" min="5" max="120" value={qf.len}
                                           onChange={(e) => setQf({...qf, len: e.target.value})}/>
                                </Field>
                                <Field label={t.qfCount}>
                                    <input type="number" min="1" max="14" value={qf.count}
                                           onChange={(e) => setQf({...qf, count: e.target.value})}/>
                                </Field>
                                <Field label={t.qfAfter}>
                                    <input type="number" min="0" max="14" value={qf.after}
                                           onChange={(e) => setQf({...qf, after: e.target.value})}/>
                                </Field>
                                <Field label={t.qfBreak}>
                                    <input type="number" min="0" max="120" value={qf.brk}
                                           onChange={(e) => setQf({...qf, brk: e.target.value})}/>
                                </Field>
                                <button type="button" className="dg-btn line" onClick={runQuickFill}>
                                    {t.qfGo}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* toolbar */}
                    <div className="dg-bar">
                        <h3>{t.listTitle}</h3>
                        <span className="dg-count">
                            {rows.length} {t.countU}
                        </span>
                        <div className="dg-spacer"/>
                    </div>

                    {/* rows */}
                    <div className="dg-tbl">
                        <div className="dg-thead">
                            <div/>
                            <div>{t.cName}</div>
                            <div>{t.cTime}</div>
                            <div>{t.cMusic}</div>
                            <div>{t.cDur}</div>
                            <div/>
                        </div>

                        {!rows.length ? (
                            <div className="dg-empty">
                                <b>{t.emptyT}</b>
                                {t.emptyS}
                            </div>
                        ) : (
                            rows.map((r, i) => (
                                <div
                                    key={r.id}
                                    className={
                                        "dg-row" +
                                        (clashSet.has(r.id) ? " clash" : "") +
                                        (activeId === r.id ? " focus" : "")
                                    }
                                    onFocusCapture={() => setActiveId(r.id)}
                                >
                                    <div className="dg-idx">{i + 1}</div>

                                    <div className="f-name">
                                        <input
                                            type="text"
                                            data-focus={r.id}
                                            value={r.name}
                                            placeholder={t.namePh}
                                            aria-label={t.cName}
                                            onChange={(e) => patch(r.id, "name", e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addRow(r.id);
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="f-time">
                                        <input
                                            type="time"
                                            value={r.time}
                                            aria-label={t.cTime}
                                            onChange={(e) => patch(r.id, "time", e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addRow(r.id);
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="f-music">
                                        <MusicSelect
                                            items={musicList}
                                            value={r.music}
                                            t={t}
                                            preview={preview}
                                            rowKey={`m${r.id}`}
                                            onChange={(item) => patch(r.id, "music", item)}
                                        />
                                    </div>

                                    <div className="dg-dur" title={t.durLbl}>
                                        <input
                                            type="number"
                                            min="1"
                                            max="300"
                                            step="1"
                                            inputMode="numeric"
                                            value={r.dur}
                                            aria-label={t.durLbl}
                                            onChange={(e) => patch(r.id, "dur", e.target.value)}
                                        />
                                        <span>{t.sec}</span>
                                    </div>

                                    <div className="dg-acts">
                                        <button type="button" className="play" onClick={() => ringOne(r)}
                                                aria-label={t.listen}>
                                            <PlayIcon/>
                                        </button>
                                        <button
                                            type="button"
                                            className="del"
                                            onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                                            aria-label="Delete"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    {clashSet.has(r.id) && <div className="dg-clash">⚠ {t.clash}</div>}
                                </div>
                            ))
                        )}

                        <button type="button" className="dg-add" onClick={() => addRow()}>
                            + {t.addRow}
                        </button>
                        <p className="dg-hint">{t.hint}</p>
                    </div>
                </div>

                {/* footer */}
                <footer className="dg-foot">
                    <div className="dg-sum">
                        {placed.length > 0 && (
                            <>
                                <b>{placed.length}</b> {t.sumA} · {t.sumB}{" "}
                                <b>{pretty(toMin(placed[0].time))}</b> · {t.sumC}{" "}
                                <b>{pretty(toMin(placed[placed.length - 1].time))}</b>
                            </>
                        )}
                    </div>
                    <div className="dg-spacer"/>
                    <button type="button" className="dg-btn ghost" onClick={close}>
                        {t.cancel}
                    </button>
                    <button type="button" className={"dg-btn line" + (dayPlaying ? " hot" : "")} onClick={playDay}>
                        {dayPlaying ? <>■ {t.stopAll}</> : <>▶ {t.previewDay}</>}
                    </button>
                    <button type="button" className="dg-btn go" onClick={save} disabled={!rows.length}>
                        {t.save}
                    </button>
                </footer>

                {toast && <div className="dg-toast">{toast}</div>}
            </div>
        </div>
    );
}

export default BellScheduleModal;
